<script setup lang="ts">
// The `list_table` widget — ports v3 `CustomDashboardListTable.vue` (284 ln).
//
// What the port removes:
//   • the hand-rolled `<table>` at `:30-70` — module 02's `DataTable` owns markup, sticky header,
//     resize and the row windowing that used to be a manual `rows += 20` inside a scroll handler.
//   • `formatFixed(...)` + `` `+${formattedResult}%` `` at `:227-233` — string-built, unlocalised
//     percentages. `useFormat().formatPercent` owns them.
//   • the `formattedResult.includes('∞')` guard at `:229`: the component divided first and then
//     sniffed the FORMATTED STRING for an infinity glyph to decide whether the number was real.
//     That check is locale-dependent — a locale that renders infinity any other way falls through
//     and prints garbage. It is now a typed guard on the number itself (`changeRatio` below).
//   • its own `getReportData` fetch and `firstRequest` flag — 08's `useReportData` owns those.
//
// One deliberate narrowing: legacy resolved the selector column lists through `getPartitionColumns`
// (every column of the partition) and fell back to `mapColumns` over the widget's own fields only
// when the level did not resolve. 08's barrel exports `mapColumns` and `getSavedFieldRoleFields`
// but NOT `getPartitionColumns`, so this ships the fallback branch alone — the selectors list the
// widget's configured columns rather than the whole partition. Reaching past the barrel for it
// would be the exact boundary violation this module exists to remove; the ask is filed against 08.
import { computed, ref, shallowRef, watch } from 'vue'

import type { DashboardWidget } from '../../models/widget.types'
import type { WidgetSheet } from '../../models/widgetExport.types'
import {
  widgetColumnFields,
  widgetToReportSpec,
  widgetWorkspaceIds,
} from '../../service/widgetSpec'
import type { WidgetRenderContext } from '../../service/widgetSpec'

import {
  getSavedFieldRoleFields,
  mapColumns,
  toReportColumnOptions,
  useMetricCatalog,
  useReportCellFormatter,
  useReportData,
  type ReportColumn,
  type ReportRow,
  type ReportSpec,
} from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import {
  Alert,
  Button,
  DataTable,
  Select,
  type SelectOption,
  type TableColumn,
  type UiAsyncState,
} from '@/shared/ui'

/** Legacy's server page size (`rows: 200`). `first` advanced by 250 there — an off-by-50 that
 *  skipped a slice of every page after the first; paging by the size it requests fixes it. */
const PAGE_SIZE = 200
/** Synthetic column: the change % is derived from two response arrays, never a row field. */
const CHANGE_FIELD = '__change'

const props = withDefaults(
  defineProps<{
    widget: DashboardWidget
    title?: string
    wsId?: number | string | null
    accountWorkspaces?: readonly (number | string)[]
    targetCurrency?: string | null
    defaultPostData?: string
  }>(),
  {
    title: '',
    wsId: null,
    accountWorkspaces: () => [],
    targetCurrency: null,
    defaultPostData: '',
  },
)
const { t } = useI18n()
const format = useFormat()
const catalog = useMetricCatalog()
const { formatCell } = useReportCellFormatter()

const first = ref(0)
const loaded = shallowRef<ReportRow[]>([])
const previous = shallowRef<ReportRow[]>([])
const totalRows = ref(0)
const metricField = ref<string | null>(null)
const dimensionField = ref<string | null>(null)
const sortField = ref<string | null>(null)

const context = computed<WidgetRenderContext>(() => ({
  defaultPostData: props.defaultPostData,
  wsId: props.wsId,
  accountWorkspaces: props.accountWorkspaces,
  targetCurrency: props.targetCurrency,
}))
const workspaceIds = computed(() => widgetWorkspaceIds(props.widget.wire, context.value))

const columns = computed<ReportColumn[]>(() =>
  mapColumns(
    widgetColumnFields(props.widget.wire, getSavedFieldRoleFields(props.widget.wire.fieldValues)),
    { getColumn: catalog.getColumn },
  ),
)
const dimensionColumns = computed(() => columns.value.filter((column) => !column.numeric))
const metricColumns = computed(() => columns.value.filter((column) => column.numeric))
const metricColumn = computed(
  () => metricColumns.value.find((column) => column.field === metricField.value) ?? null,
)
const dimensionColumn = computed(
  () => dimensionColumns.value.find((column) => column.field === dimensionField.value) ?? null,
)
/** Dynamic because the formatted cell belongs to whichever metric is selected. */
const metricSlot = computed(() => `cell-${metricColumn.value?.field ?? CHANGE_FIELD}`)

function options(source: readonly ReportColumn[]): SelectOption<string>[] {
  return toReportColumnOptions(source)
}

const spec = computed<ReportSpec | null>(() => {
  const dimension = dimensionColumn.value
  if (!dimension) return null
  return widgetToReportSpec(props.widget, context.value, {
    dimensions: [dimension.field],
    first: first.value,
    rows: PAGE_SIZE,
    ...(sortField.value ? { sort_field: sortField.value } : {}),
  })
})
const report = useReportData(spec, { workspaceIds })

/** Multi-workspace responses keep the legacy `combined` envelope (ported from `:204-206`). */
const payload = computed(() => report.response.value?.combined ?? report.response.value)

/**
 * The change column compares row `n` of the current period against row `n` of `previous_period`.
 * Returns a ratio, or `null` for every case legacy printed as `'-'`: a non-numeric side, a zero
 * baseline, or a non-finite quotient. This is the typed guard that replaces the `'∞'` string sniff.
 */
function changeRatio(index: number): number | null {
  const field = metricColumn.value?.field
  if (!field) return null
  const before = previous.value[index]?.[field]
  const current = loaded.value[index]?.[field]
  if (typeof before !== 'number' || typeof current !== 'number' || before === 0) return null
  const ratio = (current - before) / before
  return Number.isFinite(ratio) ? ratio : null
}

function changeLabel(index: number): string {
  const ratio = changeRatio(index)
  if (ratio === null) return t('dashboards.widget.noChange')
  return format.formatPercent(ratio, { scale: 'ratio', maxFrac: 2, signDisplay: 'always' })
}

function changeTone(index: number): 'up' | 'down' | 'flat' {
  const ratio = changeRatio(index)
  if (ratio === null || ratio === 0) return 'flat'
  return ratio > 0 ? 'up' : 'down'
}

function metricValue(row: ReportRow): string {
  const column = metricColumn.value
  return column ? formatCell(row[column.field], column, true) : ''
}

/**
 * Maps `useReportData`'s five-state machine onto `DataTable`'s `UiAsyncState` (module 08 owns the
 * mapping for its own report kinds — `useDataVisible.ts` — this widget reads 08's data layer
 * directly, so it re-derives the same narrowing rather than reaching past the barrel for it).
 * A `load-more` page keeps the already-loaded rows on screen instead of replacing them with a
 * full-table skeleton or error banner: `loading`/`error` only take over while the table is empty.
 */
const tableState = computed<UiAsyncState>(() => {
  const state = report.state.value
  if (state === 'idle' || state === 'loading')
    return loaded.value.length === 0 ? 'loading' : 'ready'
  if (state === 'error') return loaded.value.length === 0 ? 'error' : 'ready'
  if (state === 'empty') return 'empty'
  return 'ready'
})

const tableColumns = computed<TableColumn<ReportRow>[]>(() => {
  const dimension = dimensionColumn.value
  const metric = metricColumn.value
  if (!dimension || !metric) return []
  return [
    { field: dimension.field, header: dimension.header },
    { field: metric.field, header: metric.header, numeric: true, align: 'end' },
    { field: CHANGE_FIELD, header: t('dashboards.widget.change'), align: 'end' },
  ]
})

/** Page 0 replaces the accumulated set; a `load-more` page appends to it. */
watch(
  () => report.response.value,
  () => {
    const rows = Array.isArray(payload.value?.data) ? payload.value.data : []
    const prior = payload.value?.previous_period
    const priorRows = Array.isArray(prior) ? (prior as ReportRow[]) : []
    loaded.value = first.value === 0 ? rows : [...loaded.value, ...rows]
    previous.value = first.value === 0 ? priorRows : [...previous.value, ...priorRows]
    const total = payload.value?.total_rows
    totalRows.value = typeof total === 'number' ? total : loaded.value.length
  },
)

/** A reconfigured widget re-seeds the three selectors and restarts paging from the first page. */
watch(
  columns,
  (next) => {
    const wire = props.widget.wire
    const known = (field: unknown, pool: readonly ReportColumn[]): string | null =>
      typeof field === 'string' && pool.some((column) => column.field === field) ? field : null
    const metrics = next.filter((column) => column.numeric)
    const dimensions = next.filter((column) => !column.numeric)
    metricField.value = known(wire.setMetrics?.[0], metrics) ?? metrics[0]?.field ?? null
    dimensionField.value =
      known(wire.setDimensions?.[0], dimensions) ?? dimensions[0]?.field ?? null
    sortField.value = known(wire.sortField, metrics) ?? metricField.value
    first.value = 0
    loaded.value = []
    previous.value = []
  },
  { immediate: true },
)

// Changing a selector restarts paging: the accumulated rows belong to the previous request.
watch([metricField, dimensionField, sortField], () => {
  first.value = 0
})

function loadMore(): void {
  if (loaded.value.length >= totalRows.value) return
  first.value = loaded.value.length
}

function downloadData(): WidgetSheet[] {
  const cols = [dimensionColumn.value, metricColumn.value].filter(
    (column): column is ReportColumn => column !== null,
  )
  if (cols.length === 0) return []
  return [{ name: props.title, data: [...loaded.value], cols }]
}

defineExpose({ downloadData })
</script>

<template>
  <div class="dashboard-list-table flex flex-col gap-xs p-sm">
    <div class="dashboard-list-table__controls flex flex-wrap gap-xs">
      <Select
        v-model="metricField"
        size="sm"
        :label="t('dashboards.widget.metric')"
        :options="options(metricColumns)"
      />
      <Select
        v-model="dimensionField"
        size="sm"
        :label="t('dashboards.widget.dimension')"
        :options="options(dimensionColumns)"
      />
      <Select
        v-model="sortField"
        size="sm"
        :label="t('dashboards.widget.sortField')"
        :options="options(metricColumns)"
      />
    </div>

    <DataTable
      :rows="loaded"
      :columns="tableColumns"
      :state="tableState"
      :total-rows="totalRows"
      :initial-page-size="PAGE_SIZE"
      :client-sort="false"
      :interactive-rows="false"
      max-height="75vh"
      v-bind="{ ariaLabel: title || t('dashboards.widget.untitled') }"
      @load-more="loadMore"
      @retry="report.reload"
    >
      <template #cell-__change="{ rowIndex }">
        <span
          class="dashboard-list-table__change font-numeric font-semibold"
          :class="`is-${changeTone(rowIndex)}`"
        >
          {{ changeLabel(rowIndex) }}
        </span>
      </template>
      <template #[metricSlot]="{ row }">{{ metricValue(row) }}</template>
      <template #empty>{{ t('reports.section.empty') }}</template>
      <template #error>
        <Alert tone="warning">
          {{ t('reports.section.error') }}
          <Button size="sm" variant="outline" @click="report.reload">
            {{ t('reports.section.retry') }}
          </Button>
        </Alert>
      </template>
    </DataTable>
  </div>
</template>

<style scoped>
.dashboard-list-table__change.is-up {
  color: var(--ui-color-success);
}
.dashboard-list-table__change.is-down {
  color: var(--ui-color-danger);
}
.dashboard-list-table__change.is-flat {
  color: var(--ui-color-muted);
}
</style>
